import React, { useEffect, useMemo, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { MagnifyingGlassIcon, FactoryIcon, ClipboardTextIcon, PackageIcon, UserIcon, DatabaseIcon, StackIcon, CompassIcon } from "@phosphor-icons/react";
import api from "@/lib/api";

const CAT_META = {
  orders: { label: "Sales Orders", icon: ClipboardTextIcon, route: "/sales-orders", primary: "order_no", secondary: "po_number" },
  items: { label: "Items", icon: PackageIcon, route: "/masters/items", primary: "code", secondary: "description" },
  machines: { label: "Machines", icon: FactoryIcon, route: "/live", primary: "code", secondary: "machine_type" },
  customers: { label: "Customers", icon: UserIcon, route: "/masters/customers", primary: "name", secondary: "country" },
  operators: { label: "Operators", icon: UserIcon, route: "/masters/operators", primary: "name", secondary: "code" },
  lots: { label: "Lots", icon: StackIcon, route: "/inventory", primary: "lot_no", secondary: "location" },
};

// Kept in sync with `NAV` in components/Layout.jsx — duplicated intentionally so this component stays self-contained.
const MENUS = [
  { label: "Dashboard", route: "/", hint: "Home · KPIs" },
  { label: "Live Production", route: "/live", hint: "Realtime machine board" },
  { label: "Sales Orders", route: "/sales-orders", hint: "Orders · PL · DC · Status Report" },
  { label: "Production Planning", route: "/planning", hint: "Machine allocation" },
  { label: "Loom Production Entry", route: "/entry", hint: "Daily production log" },
  { label: "Inventory", route: "/inventory", hint: "Lots · Stock" },
  { label: "Downtime", route: "/downtime", hint: "Downtime recording" },
  { label: "Maintenance", route: "/maintenance", hint: "Preventive · Breakdowns" },
  { label: "Quality", route: "/quality", hint: "Inspection · Pass/Hold/Reject" },
  { label: "Reports & Analytics", route: "/reports", hint: "Charts · Exports" },
  { label: "AI Assistant", route: "/ai", hint: "Claude Sonnet 4.5" },
  { label: "Master Data", route: "/masters/machines", hint: "Machines · Items · Customers …" },
  { label: "FG Master", route: "/articles", hint: "Finished Goods (product codes)" },
  { label: "RM Master", route: "/raw-materials", hint: "Raw Materials (incoming goods)" },
  { label: "BOM Master", route: "/bom", hint: "Bill of Materials per FG article" },
];

export default function GlobalSearch() {
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const wrapRef = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    if (q.trim().length < 2) { setData(null); return; }
    const t = setTimeout(async () => {
      setLoading(true);
      try {
        const { data } = await api.get(`/search?q=${encodeURIComponent(q)}`);
        setData(data);
      } catch { setData(null); }
      setLoading(false);
    }, 250);
    return () => clearTimeout(t);
  }, [q]);

  useEffect(() => {
    const onClick = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  const goto = (route) => { navigate(route); setOpen(false); setQ(""); };

  // Local menu filter — no API needed.
  const menuMatches = useMemo(() => {
    const s = q.trim().toLowerCase();
    if (s.length < 1) return [];
    return MENUS.filter(m => m.label.toLowerCase().includes(s) || m.hint.toLowerCase().includes(s));
  }, [q]);

  const backendTotal = (data && data.total) || 0;
  const grandTotal = backendTotal + menuMatches.length;
  const hasResults = grandTotal > 0;

  return (
    <div className="relative flex-1 max-w-xl" ref={wrapRef}>
      <MagnifyingGlassIcon size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500 z-10" />
      <input
        data-testid="global-search"
        value={q}
        onChange={(e) => { setQ(e.target.value); setOpen(true); }}
        onFocus={() => setOpen(true)}
        placeholder="Search menus, orders, items, machines, customers, operators, lots…"
        className="w-full h-9 pl-9 pr-3 bg-[var(--bg-2)] border border-[var(--border)] rounded-sm text-sm placeholder:text-zinc-500 focus:outline-none focus:border-[var(--accent)]"
      />
      {open && q.trim().length >= 1 && (
        <div className="absolute top-full mt-1 left-0 right-0 max-h-[70vh] overflow-y-auto bg-[var(--bg-1)] border border-[var(--border)] rounded-sm shadow-2xl z-50" data-testid="search-results">
          {/* Menus section — always first, always available */}
          {menuMatches.length > 0 && (
            <div className="border-b border-[var(--border)]">
              <div className="px-3 py-2 flex items-center gap-2 bg-[var(--bg-2)] overline">
                <CompassIcon size={12} weight="bold" />Menus · {menuMatches.length}
              </div>
              {menuMatches.map((m, i) => (
                <button
                  key={m.route}
                  onClick={() => goto(m.route)}
                  data-testid={`search-hit-menus-${i}`}
                  className="w-full flex items-center gap-3 px-3 py-2 text-left hover:bg-[var(--bg-2)] text-sm"
                >
                  <span className="font-mono text-xs w-40 truncate">{m.label}</span>
                  <span className="text-zinc-400 text-xs truncate">{m.hint}</span>
                </button>
              ))}
            </div>
          )}

          {q.trim().length >= 2 && loading && <div className="p-4 text-xs text-zinc-500">Searching…</div>}

          {!loading && q.trim().length >= 2 && data && backendTotal > 0 && (
            <>
              {Object.entries(CAT_META).map(([key, meta]) => {
                const rows = data[key] || [];
                if (rows.length === 0) return null;
                const Icon = meta.icon;
                return (
                  <div key={key} className="border-b border-[var(--border)] last:border-0">
                    <div className="px-3 py-2 flex items-center gap-2 bg-[var(--bg-2)] overline">
                      <Icon size={12} weight="bold" />{meta.label} · {rows.length}
                    </div>
                    {rows.map((r, i) => (
                      <button
                        key={r.id || i}
                        onClick={() => goto(meta.route)}
                        data-testid={`search-hit-${key}-${i}`}
                        className="w-full flex items-center gap-3 px-3 py-2 text-left hover:bg-[var(--bg-2)] text-sm"
                      >
                        <span className="font-mono text-xs w-32 truncate">{r[meta.primary]}</span>
                        <span className="text-zinc-400 text-xs truncate">{r[meta.secondary]}</span>
                      </button>
                    ))}
                  </div>
                );
              })}
            </>
          )}

          {!loading && !hasResults && q.trim().length >= 2 && (
            <div className="p-6 text-center text-sm text-zinc-500">No results for "{q}"</div>
          )}

          {hasResults && (
            <div className="p-2 border-t border-[var(--border)] text-[10px] text-zinc-500 text-center">
              {grandTotal} result{grandTotal > 1 ? "s" : ""}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
